import styled, { css } from "styled-components";

export const H1 = styled.h1`
  font-size: 48px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 1.44px;

  ${({ theme }) => theme.breakpoints.md.query} {
    font-size: 44px;
  }

  ${({ theme }) => theme.breakpoints.sm.query} {
    font-size: 40px;
  }
`;

export const H2 = styled.h2`
  font-size: 39px;
  font-weight: 500;
  line-height: 100%;
  letter-spacing: 1.17px;

  ${({ theme }) => theme.breakpoints.md.query} {
    font-size: 37px;
  }
`;

export const H3 = styled.h3`
  font-size: 32px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0.96px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;

  ${({ theme }) => theme.breakpoints.md.query} {
    font-size: 30px;
    letter-spacing: 0.9px;
  }
`;

export const H4 = styled.h4`
  font-size: 27px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0.84px;

  ${({ theme }) => theme.breakpoints.md.query} {
    font-size: 28px;
  }
`;

export const VariantWordSpan = styled.span`
  color: ${({ theme }) => theme.colors.primary.default};
  border-radius: 3px;
  padding: 0 2px;
`;

export const H5 = styled.h5`
  font-size: 24px;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: 0.72px;
`;

export const H6 = styled.h6`
  font-size: 19px;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: 0.57px;
`;

export const P = styled.p<{
  $small?: boolean;
  $bold?: boolean;
  $color?: "standard" | "light" | "lightest";
  $textAlign?: string;
}>`
  font-weight: ${({ $bold }) => ($bold ? "900" : "550")};
  line-height: 150%;
  ${({ $small }) =>
    $small
      ? css`
          font-size: 14px;
        `
      : css`
          font-size: 17px;
        `}
  ${({ $color, theme }) => {
    const colorStore = theme.colors.default;
    let colorKey: string | number | undefined;
    switch ($color) {
      case "lightest":
        colorKey = 300;
        break;
      case "light":
        colorKey = 400;
        break;
      case "standard":
        colorKey = "default";
    }

    return css`
      color: ${colorKey ? colorStore[colorKey] : "currentColor"};
    `;
  }}
    ${({ $textAlign }) => css`
    text-align: ${$textAlign};
  `}
`;

export const Bold = styled.strong`
  font-weight: 700;
`;

export const Italic = styled.em``;

export const Underline = styled.u``;

export const OrderedList = styled.ol`
  /* hide default marker, we’ll draw our own */
  list-style: none;
  margin: 0;
  padding-left: 1.5rem;

  /* start a fresh counter scope for EACH <ol> */
  counter-reset: item;

  > li {
    list-style: none;
    position: relative;
    counter-increment: item; /* increment at every li in this scope */
    margin: 0.25em 0 0.25em 0;
    padding-left: 0.5rem; /* space between number and content */

    /* draw hierarchical number: 1., 3.1., 3.2.1., etc. */
    &::before {
      content: counters(item, ".") ". ";
      position: absolute;
      left: -3.5rem;
      width: 3.5rem;
      text-align: right;
      opacity: 0.65;
      font-weight: 600;
    }

    /* nested lists get their OWN counter scope,
       but still participate in 'counters(item, ".")' so you get 3.1, 3.2, ... */
    > ol {
      counter-reset: item;
      list-style: none;
      margin: 0.25em 0;
      padding-left: 1.5rem;
    }
  }
`;

export const Variant = styled.span`
  color: ${({ theme }) => theme.colors.primary.default};
  background: ${({ theme }) =>
    `color-mix(in srgb, ${theme.colors.primary.default} 8%, transparent)`};
  border-radius: 3px;
  padding: 0 2px;
`;

export const UnorderedList = styled.ul`
  /* reset browser defaults and ensure clean nesting */
  list-style: none;
  margin: 0;
  padding-left: 1.5rem; /* same left padding as ordered list for alignment */

  li {
    list-style: none;
    position: relative;
    margin: 0.25em 0;
    padding-left: 0.75rem; /* gap between bullet and text */
    line-height: 1.5;
    display: block;

    /* custom bullet */
    &::before {
      content: "";
      position: absolute;
      left: -1.25rem; /* aligns bullet inside same gutter as numbers */
      top: 0.65em;
      width: 0.45em;
      height: 0.45em;
      border-radius: 50%;
      background-color: currentColor;
      opacity: 0.6;
    }

    /* nested unordered list (same bullet style but properly indented) */
    > ul {
      list-style: none;
      margin: 0.25em 0;
      padding-left: 1.5rem;
    }

    /* nested ordered list inside unordered (valid structure) */
    > ol {
      list-style: none;
      margin: 0.25em 0;
      padding-left: 1.5rem;
    }
  }
`;

export const Heading = styled.h5`
  font-size: 1.5em;
  font-weight: 700;
`;

export const Quote = styled.blockquote`
  padding: 12px;
  position: relative;
  background: color-mix(in srgb, currentColor 10%, transparent);
  border: 1px solid color-mix(in srgb, currentColor 15%, transparent);
  border-radius: 0.6em;
  width: fit-content;
`;

export const Link = styled.a`
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.primary.default};

  &:hover {
    color: ${({ theme }) => theme.colors.primary[700]};
  }
`;
